import { db } from "../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
} from "firebase/firestore";

/**
 * AdOrchestrator manages the playback of localized/targeted audio advertisements
 * independently of the main circadian soundscape.
 *
 * It listens for ad configurations and periodically injects audio spots
 * into the audio context.
 */

export interface AdCampaign {
  id: string;
  audioUrl: string;
  frequencyMinutes: number;
  targetedRegions: string[];
  active: boolean;
  lastPlayedAt?: number;
}

class AdOrchestrator {
  private adCampaigns: AdCampaign[] = [];
  private adAudioElement: HTMLAudioElement | null = null;
  private isAdPlaying: boolean = false;
  private shuffleBag: AdCampaign[] = [];
  private onAdStart: () => void;
  private onAdEnd: () => void;

  constructor(onAdStart: () => void, onAdEnd: () => void) {
    this.onAdStart = onAdStart;
    this.onAdEnd = onAdEnd;
    this.init();
  }

  private async init() {
    // Listen for campaign updates
    const adQuery = query(
      collection(db, "ad_campaigns"),
      where("active", "==", true),
    );
    onSnapshot(adQuery, (snapshot) => {
      this.adCampaigns = snapshot.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() }) as AdCampaign,
      );
      console.log(
        "[AdOrchestrator] Campaigns updated:",
        this.adCampaigns.length,
      );
    });
  }

  public async playScheduledAd(region: string) {
    if (this.isAdPlaying) return;

    // Filter by region (assuming 'generic' includes all regions)
    const eligibleAds = this.adCampaigns.filter(
      (ad) =>
        ad.targetedRegions.includes(region) ||
        ad.targetedRegions.includes("generic"),
    );
    if (eligibleAds.length === 0) return;

    // Shuffle Bag pattern
    let bag = this.shuffleBag;
    // Keep only currently eligible ads in our bag
    bag = bag.filter((bagAd) => eligibleAds.some((el) => el.id === bagAd.id));

    if (bag.length === 0) {
      bag = [...eligibleAds].sort(() => Math.random() - 0.5);
    }

    const adToPlay = bag.pop();
    this.shuffleBag = bag;

    if (adToPlay) {
      await this.triggerAd(adToPlay);
    }
  }

  private async triggerAd(ad: AdCampaign) {
    this.isAdPlaying = true;
    this.onAdStart(); // Duck main volume

    if (!this.adAudioElement) {
      this.adAudioElement = new Audio();
      this.adAudioElement.crossOrigin = "anonymous";
    }

    this.adAudioElement.src = ad.audioUrl;
    this.adAudioElement.onended = () => {
      this.isAdPlaying = false;
      this.onAdEnd(); // Restore main volume
    };

    this.adAudioElement.onerror = () => {
      console.error("[AdOrchestrator] Audio Error");
      this.isAdPlaying = false;
      this.onAdEnd();
    };

    try {
      await this.adAudioElement.play();
    } catch (err) {
      console.error("[AdOrchestrator] Failed to play ad", err);
      this.isAdPlaying = false;
      this.onAdEnd();
    }
  }
}

export default AdOrchestrator;
